// ADP (Ontario Assistive Devices Program) hearing-claim model.
// The deterministic core of "Prestin does your ADP paperwork for you":
// given a patient record + the wedge's invoice read, compute the funding
// split, the 5-year funding-period clock, and the populated application +
// invoice-file fields for form 014-3224-67 (post-March-2026 version).
//
// SOURCE OF TRUTH: research/adp-mechanics-2026-07-06.md (25/25 primary-source
// claims verified). Where a rule is not fully pinned from a primary source it
// is flagged in-line and surfaced as a Cell with source "pending-form-spec".
//
// PHI BOUNDARY: this module is pure and deterministic on purpose. It is fed
// patient identity (health number, name, DOB) but holds none, and it MUST NOT
// be reachable from any model-read payload (modelReadPayload allow-list,
// docs/14 threat model + ADR 0006). No model ever sees an ADP claim.

// Money is integer cents everywhere internally; floats never touch a dollar.
export type Cents = number;

export type FittingType = "monaural" | "binaural" | "fm";
export type Ear = "left" | "right";

// 75% = 3/4, kept as an exact integer ratio so the contribution never drifts.
export const ADP_RATE_NUM = 3;
export const ADP_RATE_DEN = 4;

// Feb 2026 Hearing Devices manual: 75% up to $500 monaural, $1000 binaural,
// $1350 FM system. Caps are on the ADP contribution, not on the device cost.
export const ADP_CAPS: Record<FittingType, Cents> = {
  monaural: 50000,
  binaural: 100000,
  fm: 135000,
};

// Designated funding period: Hearing Aid and FM system are both 5 years.
export const FUNDING_PERIOD_YEARS: Record<FittingType, number> = {
  monaural: 5,
  binaural: 5,
  fm: 5,
};

export interface DeviceAid {
  ear?: Ear;          // omitted for an FM system
  make: string;
  model: string;
  serial: string;     // the security-gated field; a claim cannot go out without it
  costCents: Cents;   // this aid's price to the client, before the ADP split
  qtyNote?: string;   // set when lifted from an invoice line with qty > 1
}

export interface Applicant {
  name: string;
  healthNumber: string;   // OHIP: 10 digits (+ optional 2-letter version code)
  dateOfBirth: string;    // ISO yyyy-mm-dd
  address: string;
}

export interface Authorizer {
  name: string;
  registrationNumber: string;  // CASLPO registration of the authorizing audiologist
  category: string;            // ADP authorizer category for hearing aids
}

export interface Vendor {
  name: string;
  adpVendorNumber: string;
  invoiceNumber: string;   // the vendor's own invoice number
  invoiceDate: string;     // ISO
  deliveryDate: string;    // ISO
}

export interface AdpClaimInput {
  fittingType: FittingType;
  aids: DeviceAid[];
  applicant: Applicant;
  authorizer: Authorizer;
  vendor: Vendor;
  priorFundingDate?: string | null;  // ISO of the last ADP-funded fitting, if any
}

export interface AdpFunding {
  fittingType: FittingType;
  totalCostCents: Cents;
  cap: Cents;
  rawSeventyFiveCents: Cents;   // round(cost * 3/4) before the cap bites
  contributionCents: Cents;     // min(cap, rawSeventyFive) — what ADP pays the vendor
  clientPaysCents: Cents;       // cost - contribution — the balance the vendor collects
  cappedOut: boolean;           // the $-cap bound rather than the 75% rate
}

export interface AdpEligibility {
  fundingPeriodYears: number;
  priorFundingDate: string | null;
  eligibleFrom: string | null;  // priorFundingDate + period, ISO; null when no prior claim
  asOf: string;                 // ISO
  eligible: boolean;
  daysRemaining: number;        // > 0 means still inside the period (blocked)
}

// A cell of the government paperwork. "manual" = a person must enter or sign it
// on the actual form (Prestin never auto-signs). "pending-form-spec" = a field
// we believe exists but cannot finalize until the real 014-3224-67 PDF / the
// published E-Claim Technical Specification is read (research open items).
export type CellSource = "record" | "computed" | "manual" | "pending-form-spec";

export interface Cell {
  label: string;
  value: string | null;
  required: boolean;
  source: CellSource;
}

// The comma-delimited .txt invoice file that accompanies the XML on upload.
// The Technical Specification enumerates 16 required fields; the research
// pinned down 7 of them by name. The rest await the published field list.
export interface InvoiceTxtRecord {
  totalFields: 16;
  confirmed: { label: string; value: string | null }[];
  pendingFieldNote: string;
}

export interface AdpClaim {
  form: "014-3224-67";
  formVersion: "2026-03-02";   // prescriber section removed; attestation added
  attestationForm: "014-ON00881E";
  funding: AdpFunding;
  eligibility: AdpEligibility;
  application: Cell[];         // the on-form fields, populated where we can
  invoiceTxt: InvoiceTxtRecord;
  blockers: string[];          // hard data problems that stop the claim
  manualActions: string[];     // signatures / attestation a human must complete
}

export const toCents = (dollars: number): Cents => Math.round(dollars * 100);
export const fromCents = (cents: Cents): number => cents / 100;
export const money = (cents: Cents): string =>
  (cents < 0 ? "-$" : "$") + (Math.abs(cents) / 100).toFixed(2);
